import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import VendorCTAsGroup from '../VendorCTAsGroup/VendorCTAsGroup';
import { selectVendorCTAs } from '../../redux/vendorCTAsReducer';
import { VEHICLE_FALLBACK_IMAGE, VEHICLE_FALLBACK_IMAGE_FR } from '../../constants';
import { FilteredVehicle, GalleryImagesType, Rebate } from '../../types';
import Modal from '../Modal';
import Location from './Location';
import { CpoIcon, Legal, TextContainer } from './VehicleCard.styles';
import {
  VehicleName,
  VehicleInfo,
  CardContentPadding,
  VehicleTrim,
  Milage,
  CpoBLOCK,
  T3StatusContainer,
  StockCpoHeader,
  CardContainer,
  ImageContainer,
  SingleVehicleImage,
  VdpCtaContainer,
  VdpCTA,
  DisclaimerTitleWrapper,
} from './T3Card.styles';
import CPOLogo from '../../assets/images/CertifiedBadges/CPO';
import ImagesGalleryT3 from '../ImagesGallery/ImagesGalleryT3';
import Status from './Status';
import PriceBreakdown from './PriceBreakdown/PriceBreakdown';
import { COLORS } from '../../styles/variables';
import { HeadlineOrderTwo } from '../Common/Common.style';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { CarType, ImageType, NonAudiDealershipLocation } from '../../types/oneGraphTypes';
// import { testGalleryImages as images } from '../../constants/testGalleryImages';

interface VehicleCardProps {
  vehicle: CarType;
  marketType: string;
  vehicleDetailsUrl: string;
  isImageGalleryOn: boolean;
  mainDealer?: string;
}

const T3VehicleCard: React.FC<VehicleCardProps> = ({
  vehicle,
  marketType,
  isImageGalleryOn,
  vehicleDetailsUrl,
  mainDealer,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';
  const isEnglish = i18n.language === 'en';
  const { stockCar, geoDistance } = vehicle;
  const {
    id,
    cartypeText,
    salesInfo,
    dealer,
    carPrices,
    images,
    titleText,
    mileage,
    model,
    vin,
    subtitleText,
    dynamicAttributes,
    qualityLabel,
  } = stockCar;

  const { orderStatusText, availableFromDateInfo } = salesInfo;
  const name = model?.name;

  const { vendorCtaGroup } = useSelector(selectVendorCTAs);
  const { listOfDealers, isTier3page } = useSelector(selectAppConfig);

  const isMultipleDealers = listOfDealers.length > 1;

  const isMarketCA = marketType === 'CAN';
  const isNewVehicle = cartypeText === 'N';
  const isAvailableNow = availableFromDateInfo?.value === 'Immediately';
  const vehicleType = isNewVehicle ? 'new' : 'used';

  const isCPO = !!(!isNewVehicle && qualityLabel.length && qualityLabel[0].label === 'CPO');
  const vehicleDataType = cartypeText === 'U' && isCPO ? 'cpo' : vehicleType;

  const isReservedVehicle = salesInfo.saleOrderTypeText?.includes('SOLD');
  const vehicleDealerImages = images?.filter((image) => image?.id?.group === 'dealerImages');

  const hasDealerImages = !!vehicleDealerImages?.length;

  const sortedImages = useMemo(() => {
    const listToSort = ['sc5c01', 'vtp4x3n3c', 'vtp4x3n5c'];
    return images?.length
      ? [...images].sort((a, b) => {
          const indexA = listToSort.indexOf(a.id.image);
          const indexB = listToSort.indexOf(b.id.image);
          return (indexA === 1 ? Infinity : indexA) - (indexB === 1 ? Infinity : indexB);
        })
      : [];
  }, [images]);

  const vehicleFallbackImage = lang === 'fr' ? VEHICLE_FALLBACK_IMAGE_FR : VEHICLE_FALLBACK_IMAGE;

  // first image url from sorted list used as model image if no other images are available
  // to use in single image scenario
  const modelImageFromScs = hasDealerImages ? sortedImages[0]?.url : vehicleFallbackImage;

  const modelMileage = mileage.value.formatted;

  const isQ5Hybrid = name?.includes('Q5') && name?.includes('TFSI e');
  const modelName = isQ5Hybrid ? `${titleText} Plug-in Hybrid` : titleText;

  // OADD (Tier 3)Stock Number
  const stockNumber = useMemo(() => {
    const stockNumberObject = dynamicAttributes?.find((key) => key?.id === 'VEHICLE_ID');
    if (stockNumberObject) {
      return stockNumberObject?.value;
    }
    return null;
  }, [dynamicAttributes]);

  const nonAudiDealershipLocation = useMemo(() => {
    const defaultResult = {
      multiLocation: false,
      dealerName: '',
      dealerId: '',
    } as NonAudiDealershipLocation;
    // Find NAD Object in dynamic attributes
    const nadObject = dynamicAttributes?.find((key) => key?.id === 'NAD');
    if (!nadObject) return defaultResult;
    // Safely parse the value and handle invalid JSON
    const parsedNadObject = (() => {
      try {
        return JSON.parse(nadObject?.value);
      } catch {
        return null;
      }
    })();
    if (!parsedNadObject) return defaultResult;

    return {
      multiLocation: parsedNadObject?.Multi_location === 'Yes',
      dealerName: parsedNadObject?.Dealer_Name || '',
      dealerId: parsedNadObject?.Audi_Parent_ID || '',
    } as NonAudiDealershipLocation;
  }, [dynamicAttributes]);

  const rebate = useMemo(() => {
    const rebates: Rebate[] = [];
    if (!dynamicAttributes) return [];
    const RebateKey = lang === 'fr' ? 'FR-REBATE' : 'REBATE';

    dynamicAttributes?.forEach((attribute) => {
      if (attribute?.id?.startsWith(RebateKey)) {
        try {
          const rebateItem = JSON.parse(attribute?.value);
          rebates.push({
            Value: rebateItem?.Amount,
            Name: rebateItem?.OfferName,
            offerDescription: rebateItem?.OfferDescription,
            offerStartDate: rebateItem?.OfferStartDate,
            offerEndDate: rebateItem?.OfferEndDate,
          });
        } catch (error) {
          console.error(`Failed to parse rebate data for key ${attribute?.id}:`, error);
        }
      }
    });
    return rebates;
  }, [dynamicAttributes, lang]);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    text: string | JSX.Element;
  }>({
    isOpen: false,
    title: '',
    text: '',
  });

  const priceLegalText = useMemo(() => {
    if (marketType === 'CAN' && isNewVehicle && !isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'CAN' && isNewVehicle && isAvailableNow)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'CAN' && !isNewVehicle)
      return t('vlp.vehicleResults.vehicleCard.dealerListPriceLegalText');

    if (marketType === 'USA' && isNewVehicle)
      return t('vlp.vehicleResults.vehicleCard.msrpLegalTextUSA');

    // marketType === 'USA' && vehicleType === 'used'
    return (
      <Trans
        i18nKey="vlp.vehicleResults.vehicleCard.dealerPriceLegalText"
        // the global reset css imported in several feature apps
        // contains globally applied oppinionated styling
        components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
      />
    );
  }, [marketType, isNewVehicle, isAvailableNow, t]);

  const onModalOpen = useCallback(
    (e: any, title?: string, text?: string | JSX.Element) => {
      e.preventDefault();
      e.stopPropagation();
      setModalState({
        isOpen: true,
        title: title || '',
        text: text || priceLegalText || '',
      });
    },
    [priceLegalText],
  );

  const onModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      title: '',
      text: '',
    });
  }, []);

  const popoverDescription = useMemo(() => {
    if (isReservedVehicle) return t('vlp.vehicleResults.vehicleCard.reserved');
    if (nonAudiDealershipLocation && nonAudiDealershipLocation?.multiLocation)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.nonAudiLocationPopUpInfo');
    if (isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowCAN.popoverDescription');
    if (isAvailableNow && !isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableNowUSA.popoverDescription');
    if (!isAvailableNow && isMarketCA)
      return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionCAN');
    return t('vlp.vehicleResults.vehicleCard.availableSoon.popoverDescriptionUSA');
  }, [isAvailableNow, isMarketCA, isReservedVehicle, nonAudiDealershipLocation, t]);

  const altFunction = useCallback(() => {
    if (!modelImageFromScs) {
      return `${t('vlp.vehicleResults.vehicleCard.vehicleFallbackAlt')} ${titleText} `;
    }
    return `${t('vlp.vehicleResults.vehicleCard.vehicleStandardAlt')} ${titleText} `;
  }, [modelImageFromScs, titleText, t]);

  const statusForDot = useMemo(() => {
    if (isReservedVehicle) return 'REVERVED';
    if (isAvailableNow) return 'AVAILABLE';
    return 'PIPELINE';
  }, [isAvailableNow, isReservedVehicle]);

  const vehicleStatus = useMemo(() => {
    if (isReservedVehicle) return 'Reserved';
    if (nonAudiDealershipLocation && nonAudiDealershipLocation?.multiLocation)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.nonAudiLocation');
    if (isAvailableNow)
      return t(`vlp.vehicleResults.vehicleCard.availableNow${marketType}.availabilityLabel`);
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Intransit From Factory')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.factory');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Port Stock')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.port');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText === 'Intransit Inland')
      return t('vlp.vehicleResults.vehicleCard.orderStatus.inland');
    if (!isAvailableNow && vehicleType === 'new' && orderStatusText == null)
      return t('vlp.vehicleResults.vehicleCard.orderStatus.contactDealer');

    return '';
  }, [
    isReservedVehicle,
    nonAudiDealershipLocation,
    t,
    isAvailableNow,
    marketType,
    vehicleType,
    orderStatusText,
  ]);

  // Decide which images to use in the gallery
  // If new vehicle with multiple images use all sorted images from scs
  // If new vehicle with no images use fallback image
  // If used vehicle with dealer images use dealer images
  // If used vehicle with no dealer images use fallback image if no images from scs
  const galleryImagesVar = useMemo(() => {
    if (images?.length > 1 && isNewVehicle) return sortedImages;
    if (images?.length > 1 && hasDealerImages) return vehicleDealerImages;

    const modelImage = modelImageFromScs || vehicleFallbackImage;
    const singleImageObject: ImageType[] = [
      {
        id: { group: 'renderImagesPNG', image: 'vtp4x3n3c' },
        type: 'render',
        url: modelImage,
      },
    ];
    return singleImageObject;
  }, [
    hasDealerImages,
    images,
    isNewVehicle,
    modelImageFromScs,
    sortedImages,
    vehicleDealerImages,
    vehicleFallbackImage,
  ]);

  // Decide which image to use for single image scenario
  const modelImage = useMemo(() => {
    if (isTier3page && isNewVehicle && vehicleDealerImages?.length)
      return vehicleDealerImages[0]?.url;
    if (isTier3page && isNewVehicle && sortedImages?.length) return sortedImages[0]?.url;
    // no dealerImages so use first jelly bean image from scs
    if (modelImageFromScs) return modelImageFromScs;
    // fallback image if no images from scs
    return vehicleFallbackImage;
  }, [
    isNewVehicle,
    isTier3page,
    modelImageFromScs,
    sortedImages,
    vehicleDealerImages,
    vehicleFallbackImage,
  ]);

  const cpoType = useMemo(() => {
    if (isMarketCA) {
      return isEnglish ? 'CPO_CAN_EN' : 'CPO_CAN_FR';
    }
    return 'CPO_USA';
  }, [isMarketCA, isEnglish]);

  const unitOfMeasurement = marketType === 'CAN' ? 'km' : 'miles';

  const renderCpoBlock = useMemo(() => {
    return (
      <CpoBLOCK data-testid={`cpo-block-${cpoType}`} isCPO={isCPO}>
        {isCPO && (
          <CpoIcon>
            <CPOLogo type={cpoType} />
          </CpoIcon>
        )}
      </CpoBLOCK>
    );
  }, [cpoType, isCPO]);

  const renderGallery = useMemo(() => {
    return (
      <ImageContainer noPadding isImageGalleryOn={isImageGalleryOn} isTier3={isTier3page}>
        {isImageGalleryOn ? (
          <ImagesGalleryT3
            isNewVehicle={isNewVehicle}
            isReservedVehicle={isReservedVehicle}
            images={galleryImagesVar}
            bodyType={null}
            alt={altFunction()}
            vehicleDetailsUrl={vehicleDetailsUrl}
            data-cy="vehicle-card-image-gallery"
          />
        ) : (
          <a href={vehicleDetailsUrl}>
            <SingleVehicleImage
              src={modelImage}
              alt={altFunction()}
              isNewVehicle={isNewVehicle}
              isReservedVehicle={isReservedVehicle}
              data-cy="vehicle-card-simgle-image"
            />
          </a>
        )}
      </ImageContainer>
    );
  }, [
    altFunction,
    galleryImagesVar,
    isImageGalleryOn,
    isNewVehicle,
    isReservedVehicle,
    isTier3page,
    modelImage,
    vehicleDetailsUrl,
  ]);

  const renderStatus = useMemo(() => {
    return (
      <Status
        popoverDescription={popoverDescription}
        vehicleStatus={vehicleStatus}
        statusForDot={statusForDot}
        iconColor={COLORS.grey60}
      />
    );
  }, [popoverDescription, statusForDot, vehicleStatus]);

  const renderStockNumberHeader = useMemo(() => {
    return (
      <div>
        {t('vlp.vehicleResults.vehicleCard.stock')} {stockNumber}
      </div>
    );
  }, [stockNumber, t]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      orderStatusText &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(orderStatusText))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      <CardContainer
        isReservedVehicle={isReservedVehicle}
        // href={vehicleDetailsUrl}
        // tabIndex={0}
        // data-cy="vehicle-card-button"
        noPadding
        relative
      >
        {!!stockNumber?.length && (
          <StockCpoHeader>
            {renderStockNumberHeader}
            {!isNewVehicle && isCPO && renderCpoBlock}
          </StockCpoHeader>
        )}

        {renderGallery}
        <CardContentPadding>
          <VehicleInfo>
            <T3StatusContainer>{renderStatus}</T3StatusContainer>
            <VehicleName data-testid="model-name" data-cy="vehicle-card-model-name">
              {!!model?.salesModelyear} {modelName}
            </VehicleName>
            <VehicleTrim data-testid="trim-name" data-cy="vehicle-card-trim-name">
              {subtitleText}
            </VehicleTrim>
            {!isNewVehicle && modelMileage && (
              <Milage data-testid="model-mileage" data-cy="vehicle-card-mileage">
                {t('vlp.vehicleResults.vehicleCard.mileageTier3', {
                  modelMileage,
                  unitOfMeasurement,
                })}
              </Milage>
            )}
            {isMultipleDealers && (
              <Location
                dealerName={dealer?.name}
                unitOfMeasurement={unitOfMeasurement}
                distanceFromUser={geoDistance?.value?.number}
                isNewVehicle={isNewVehicle}
                noFixHeight
              />
            )}
          </VehicleInfo>
          <PriceBreakdown
            typedPrices={carPrices ?? []}
            onModalOpen={onModalOpen}
            isNewVehicle={isNewVehicle}
            rebate={rebate}
          />
          <VdpCtaContainer>
            <VdpCTA href={vehicleDetailsUrl}>{t('vlp.vehicleResults.vehicleCard.ctas.vdp')}</VdpCTA>
          </VdpCtaContainer>
        </CardContentPadding>
      </CardContainer>
      {vendorCtaGroup?.length > 0 && (
        <VendorCTAsGroup
          ctas={vendorCtaGroup}
          vin={vin}
          vehicleId={id}
          market={marketType}
          mainDealer={mainDealer}
          vehicleType={vehicleDataType}
          vehicleData={stockCar}
        />
      )}
      <Modal isOpen={modalState?.isOpen} onClose={onModalClose} data-cy="vehicle-card-legal-modal">
        <Legal>
          <DisclaimerTitleWrapper>
            <HeadlineOrderTwo color={COLORS.black}>{modalState?.title}</HeadlineOrderTwo>
          </DisclaimerTitleWrapper>
          <TextContainer>{modalState?.text}</TextContainer>
        </Legal>
      </Modal>
    </>
  );
};

export default T3VehicleCard;
